// Ulture Pest — shared PDF brand tokens
//
// Centralizes the visual identity used across every PDF report so we get
// a single, consistent look across Spray Logs, Notas de Consumo and any
// future export.

import ultureLogoUrl from "@/assets/ulture-logo-transparent.png";

export const PDF_BRAND = {
  appName: "Ulture Pest",
  // RGB triplets (jsPDF expects RGB, not HSL).
  // Mirrors the app's --primary cyan and --primary-blue.
  primaryCyan: [52, 199, 196] as [number, number, number],   // hsl(177 58% 52%)
  primaryBlue: [37, 99, 192] as [number, number, number],    // hsl(213 70% 51%)
  navy: [16, 32, 58] as [number, number, number],            // dark navy text
  navySoft: [46, 64, 92] as [number, number, number],
  ink: [33, 41, 55] as [number, number, number],             // body text
  muted: [110, 118, 134] as [number, number, number],        // secondary text
  divider: [225, 230, 238] as [number, number, number],      // soft grey
  cardBg: [248, 251, 254] as [number, number, number],       // very light blue tint
  white: [255, 255, 255] as [number, number, number],
  // Semantic accents — used for BIO / CHEM chips and metric callouts.
  bioBg: [220, 246, 232] as [number, number, number],
  bioFg: [21, 128, 78] as [number, number, number],
  chemBg: [254, 235, 200] as [number, number, number],
  chemFg: [161, 96, 14] as [number, number, number],
  rowTint: [246, 250, 254] as [number, number, number],      // alt row background
  headerTint: [240, 247, 252] as [number, number, number],   // table header bg
} as const;

export const PDF_TYPO = {
  font: "helvetica",
  sizes: {
    title: 18,
    h1: 13,
    h2: 11,
    body: 9,
    small: 7.5,
    tiny: 6.5,
  },
} as const;

// ── V2 tokens — "Informe de Aplicaciones" spec (PdfReportBuilderV2) ──────────
export type RGB = [number, number, number];

/**
 * Visual tokens for the V2 PDF system. Geometry in mm, font sizes in pt.
 * Colours are RGB triplets (jsPDF native). The font family is swapped to "Sora"
 * at runtime by the builder once registerSora() succeeds; falls back to "helvetica".
 */
export const PDF_V2 = {
  page: { w: 210, h: 297 },
  margin: { top: 13, left: 11, right: 11, bottom: 16 },
  contentW: 188, // 210 - 11 - 11
  font: "helvetica", // builder overrides with "Sora" when embedded

  color: {
    ink: [15, 23, 42] as RGB,            // #0F172A body, headers, totals
    muted: [100, 116, 139] as RGB,       // #64748B labels, notes, chips
    line: [229, 231, 235] as RGB,        // #E5E7EB borders, header underline
    lineSoft: [238, 242, 246] as RGB,    // #EEF2F6 row separators
    appTint: [246, 251, 251] as RGB,     // #F6FBFB application head band
    teal: [10, 167, 184] as RGB,         // #0AA7B8 day-header bar
    tealD: [11, 124, 140] as RGB,        // #0B7C8C day-count pill text
    tealSoft: [230, 245, 243] as RGB,    // #E6F5F3 day-count pill bg
    green: [22, 163, 74] as RGB,         // #16A34A app meta line (ha, €/ha)
    greenD: [21, 128, 61] as RGB,        // #15803D app subtotal
    amberBg: [254, 243, 199] as RGB,     // #FEF3C7 Químico badge fill
    amberTx: [146, 64, 14] as RGB,       // #92400E Químico badge text
    bioBg: [220, 252, 231] as RGB,       // #DCFCE7 Biológico badge fill
    bioTx: [22, 101, 52] as RGB,         // #166534 Biológico badge text
    pestDot: [225, 29, 72] as RGB,       // #E11D48 target-pest dot
    footer: [148, 163, 184] as RGB,      // #94A3B8 footer text
    white: [255, 255, 255] as RGB,
    secondary: [51, 65, 85] as RGB,      // #334155 pest name
    materia: [71, 85, 105] as RGB,       // #475569 materia activa (lighter)
    placeholder: [148, 163, 184] as RGB, // #94A3B8 "Sin objetivo indicado"
    // KPI left-tick colours by card index: blue, teal-d, green, amber
    kpiTick: [
      [38, 102, 191], [11, 124, 140], [22, 163, 74], [217, 119, 6],
    ] as RGB[],
    valChem: [180, 83, 9] as RGB,        // #B45309 Químico KPI value
    valBio: [22, 163, 74] as RGB,        // green Biológico KPI value
    grad: {
      from: [6, 182, 196] as RGB,        // #06B6C4
      mid: [22, 160, 196] as RGB,        // #16A0C4
      to: [38, 102, 191] as RGB,         // #2666BF
    },
  },

  // Type scale (pt) — dense report; small by design (spec §2)
  size: {
    brandTitle: 11.5, brandSub: 6.8,
    clientName: 8.5, dateRange: 6.8,
    chip: 6.3,
    kpiLabel: 5.7, kpiValue: 11.5, kpiNote: 5.6,
    dayDate: 8.3, dayPill: 5.9,
    locPath: 7.2, badge: 5.3, pest: 6.5, appMeta: 6.5, appSubtotal: 7.5,
    colHeader: 6.3, productName: 6.5, materia: 6.5, num: 6.5,
    dayTotalLabel: 6.5, dayTotalValue: 8.3,
    footer: 5.3,
  },
} as const;

/**
 * Las filas de SECCIÓN de las tablas compuestas (informe de Plagas): el azul claro
 * de Ulture. Con la marca de una empresa se pintan en neutro (`buildReportPdf`).
 */
export const PDF_SECCION_ULTURE = {
  fill: [234, 241, 248] as RGB,
  text: [19, 49, 74] as RGB,
};
/** Las mismas filas con marca: gris claro y tinta, sin el azul de Ulture. */
export const PDF_SECCION_NEUTRA = {
  fill: [243, 244, 246] as RGB,
  text: [15, 23, 42] as RGB,
};

// ── Logo loading ─────────────────────────────────────────────────────────────
let logoDataUrlPromise: Promise<string | null> | null = null;

/** Loads the Ulture logo once and caches it as a data URL for jsPDF. */
export async function getUltureLogoDataUrl(): Promise<string | null> {
  if (logoDataUrlPromise) return logoDataUrlPromise;
  logoDataUrlPromise = (async () => {
    try {
      const res = await fetch(ultureLogoUrl);
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  })();
  return logoDataUrlPromise;
}
